// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import type { ReactNode } from 'react'
import { usePickupLocations } from '../../hooks/queries/shipping'
import { AppTextField, SelectField } from '../ui/FormFields'

/**
 * D-SHIP-23 §10 — the carrier's pickup location (RoadPas branch) by name. One location is shown
 * locked: there is nothing to choose. Carriers without locations render `unsupported`.
 */
export default function PickupLocationField({ carrierId, pharmacyId, value, onChange, label = 'Pickup branch', unsupported = null, size = 'small' }: {
  carrierId: string
  pharmacyId?: string
  value: string
  onChange: (id: string) => void
  label?: string
  unsupported?: ReactNode
  size?: 'small' | 'medium'
}) {
  const q = usePickupLocations(carrierId, pharmacyId)
  if (q.isPending) return <AppTextField size={size} fullWidth label={label} value="Loading branches…" disabled />
  if (!q.data?.supported) return <>{unsupported}</>

  const { locations, locked, detail } = q.data
  if (locations.length === 0)
    return <AppTextField size={size} fullWidth label={label} value="" disabled error helperText={detail ?? 'No branches returned by the carrier.'} />
  if (locked)
    return <AppTextField size={size} fullWidth label={label} value={locations[0].name} InputProps={{ readOnly: true }}
      helperText="Only branch on this account — selected automatically." />

  const known = locations.some(l => l.id === value)
  return (
    <SelectField size={size} fullWidth label={label} value={known ? value : ''} onChange={onChange}
      error={!known} helperText={known ? undefined : 'Choose a branch.'}
      options={[...(known ? [] : [{ value: '', label: 'Choose a branch' }]), ...locations.map(l => ({ value: l.id, label: l.name }))]} />
  )
}

/** The branch's name for display; falls back to the stored id. */
export function PickupLocationName({ carrierId, id }: { carrierId: string; id: string | null }) {
  const q = usePickupLocations(carrierId)
  if (!id) return <>—</>
  return <>{q.data?.locations.find(l => l.id === id)?.name ?? id}</>
}
